import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Download, Info } from 'lucide-react';
import { statistik, type StatZeitraum, type StatExportArt } from '../api';

/**
 * Die Zahlen zum Mitnehmen.
 *
 * ══ VIER KNOEPFE STATT EINEM ═══════════════════════════════════════════════
 *
 * Der Reiter zeigt vier verschiedene Tabellen mit verschiedenen Spalten. In
 * eine CSV passt genau eine davon; sie untereinanderzuhaengen ergaebe eine
 * Datei mit drei Spaltenkoepfen, die keine Tabellenanwendung versteht.
 *
 * ══ IMMER TAGESWERTE ═══════════════════════════════════════════════════════
 *
 * Unabhaengig vom Zeitraum, den der Reiter gerade zeigt. Wer ausleitet, will
 * selbst gruppieren — und aus Tageswerten laesst sich jede Zusammenfassung
 * bilden, aus der Zusammenfassung die Tageswerte nicht.
 *
 * ══ WARUM UEBER DEN CLIENT UND NICHT UEBER EINEN LINK ══════════════════════
 *
 * Der Endpunkt verlangt eine Anmeldung. Ein `<a href>` schickt keinen
 * Anmeldekopf mit — die Datei kaeme als Fehlerseite an, und zwar als Datei
 * gespeichert. Also holen, als Blob ablegen, Klick ausloesen: dasselbe Muster
 * wie beim URL-Export der Domains.
 */

const ARTEN: { wert: StatExportArt; schluessel: string }[] = [
  { wert: 'tage', schluessel: 'exportDays' },
  { wert: 'seiten', schluessel: 'exportPages' },
  { wert: 'merkmale', schluessel: 'exportAttributes' },
  { wert: 'abgleich', schluessel: 'exportComparison' },
];

export default function StatistikExport({
  instanceId,
  stackName,
  zeitraum,
}: {
  instanceId: string;
  stackName?: string | null;
  zeitraum: StatZeitraum;
}) {
  const { t } = useTranslation();
  const [laeuft, setLaeuft] = useState<StatExportArt | null>(null);
  const [fehler, setFehler] = useState('');

  async function holen(was: StatExportArt) {
    setLaeuft(was);
    setFehler('');
    try {
      const blob = await statistik.exportCsv(instanceId, was, zeitraum);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const stamm = (stackName ?? instanceId).replace(/[^a-zA-Z0-9_-]/g, '-');
      a.download = `statistik-${was}-${stamm}-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setFehler(t('webstat.exportFailed'));
    } finally {
      setLaeuft(null);
    }
  }

  return (
    <div className="mt-6 rounded-lg border border-slate-200 p-4">
      <h3 className="mb-1 text-sm font-semibold text-slate-700">{t('webstat.exportTitle')}</h3>
      <p className="mb-3 flex items-start gap-1.5 text-[11px] leading-relaxed text-slate-500">
        <Info size={13} className="mt-0.5 shrink-0 text-slate-400" />
        {t('webstat.exportHint')}
      </p>

      {fehler && (
        <div className="mb-2 rounded border border-red-200 bg-red-50 p-2 text-xs text-red-700">
          {fehler}
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        {ARTEN.map((a) => (
          <button
            key={a.wert}
            type="button"
            onClick={() => holen(a.wert)}
            disabled={laeuft !== null}
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-2.5 py-1.5 text-xs text-slate-600 hover:bg-slate-50 disabled:opacity-50"
          >
            <Download size={13} className={laeuft === a.wert ? 'animate-pulse' : ''} />
            {t(`webstat.${a.schluessel}`)}
          </button>
        ))}
      </div>
    </div>
  );
}
